import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { APPOINTMENT_URL } from '../components/appointment.js';
import CrewIntakeGuide from '../components/CrewIntakeGuide.jsx';
import { getConfiguredCrewEntry } from '../components/crewPortal.js';
import { workflows } from '../data/workflows.js';

const review = workflows.find(({ id }) => id === 'peme-review');
const examination = workflows.find(({ id }) => id === 'peme-examination');

export default function Peme() {
  return (
    <>
      <Helmet>
        <title>PEME review and examinations | SeaScope</title>
        <meta name="description" content="Understand two separate PEME workflows: review completed examination documents, or prepare and complete a clinic examination with Form A, Form B and certificate." />
        <link rel="canonical" href="https://seascope.tech/peme" />
        <link rel="preload" as="image" href="/rebuild/images/peme-exam.webp" fetchPriority="high" />
      </Helmet>

      <header className="border-b border-line bg-white py-20 lg:py-28">
        <div className="container-wide grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="label-caps mb-6 block">Pre-employment medicals</span>
            <h1 className="mb-7 text-[36px] font-medium leading-[42px] lg:text-[56px] lg:leading-[64px]">
              Document review and examinations, connected
            </h1>
            <p className="mb-9 max-w-xl text-[17px] leading-7 text-ink-2">
              SeaScope supports two different jobs: assessing completed examination documents for
              a commissioned scheme, and running a clinician examination from crew intake through
              certificate. Choose the path that matches your role and agreement.
            </p>
            <a href={APPOINTMENT_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">Book a meeting</a>
          </div>
          <img
            src="/rebuild/images/peme-exam.webp"
            alt="Illustration of a seafarer at a medical examination."
            className="w-full rounded-card"
            width="1200"
            height="896"
            fetchPriority="high"
          />
        </div>
      </header>

      <section className="bg-bg-alt py-16 lg:py-24">
        <div className="container-wide">
          <h2 className="mb-10 text-[28px] font-medium lg:text-[36px]">Select the clinical workflow</h2>
          <div className="grid gap-8 md:grid-cols-2">
            <article className="rounded-card border border-line bg-white p-8 lg:p-10">
              <span className="label-caps mb-5 block">For operators and reviewers</span>
              <h3 className="mb-4 text-2xl font-medium">{review.title}</h3>
              <p className="mb-6 leading-7 text-ink-2">
                Start with completed examination documents. Compare available evidence with the
                commissioned requirements, then record an authorized clinician&apos;s disposition.
              </p>
              <Link to={review.path} className="text-link font-medium">Explore document review</Link>
            </article>
            <article className="rounded-card border border-line bg-white p-8 lg:p-10">
              <span className="label-caps mb-5 block">For clinics and seafarers</span>
              <h3 className="mb-4 text-2xl font-medium">{examination.title}</h3>
              <p className="mb-6 leading-7 text-ink-2">
                Start with crew intake and Form A, then the in-person examination, Form B and
                examiner attestation. A separate review is included only where commissioned.
              </p>
              <Link to={examination.path} className="text-link font-medium">Explore examinations</Link>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="container-wide max-w-3xl">
          <h2 className="mb-5 text-[28px] font-medium lg:text-[36px]">Records with a defined purpose</h2>
          <p className="text-[17px] leading-7 text-ink-2">
            When an authorized team links existing records for a defined care purpose, the
            document&apos;s source and the clinician&apos;s action remain identifiable. Review and
            examination have separate decisions; one does not silently complete the other.
            Our <Link to="/privacy" className="text-link">privacy policy</Link> explains how
            records are handled.
          </p>
        </div>
      </section>

      <section id="crew" className="scroll-mt-24 border-t border-line bg-bg-alt py-16 lg:py-24">
        <div className="container-wide max-w-3xl">
          <span className="label-caps mb-5 block">For seafarers</span>
          <CrewIntakeGuide entry={getConfiguredCrewEntry()} />
          <div className="mt-10 flex flex-wrap gap-6">
            <Link to="/crew-portal" className="btn-primary">Crew portal information</Link>
            <Link to="/wellness" className="text-link self-center font-medium">How Crew Wellness differs</Link>
          </div>
          <div id="crew-privacy" className="scroll-mt-24 mt-12 rounded-card border border-line bg-white p-8">
            <h3 className="mb-4 text-xl font-medium">Your answers, and who sees them</h3>
            <p className="mb-4 leading-7 text-ink-2">
              This website is informational and does not take Form A answers. Use the personal
              clinical link supplied by your agency or clinic. Ask that team who can see your
              declaration and examination record, and who receives the resulting certificate.
            </p>
            <p className="leading-7 text-ink-2">
              Read our <Link to="/privacy" className="text-link">privacy policy</Link>. It explains
              who can see your answers and where they are held.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
